import { useEffect, useState } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { RefreshCw, X } from "lucide-react";

/**
 * PWA Update Notification Component
 * 
 * Displays a notification banner when a new version of the PWA is available.
 * Allows users to reload the page to get the latest version.
 */
const PWAUpdateNotification = () => {
  const [showUpdateNotification, setShowUpdateNotification] = useState(false);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r) {
      console.log("[PWA] Service Worker registered:", r);
      // Periodically check for updates every hour
      if (r) {
        setInterval(() => {
          r.update();
        }, 60 * 60 * 1000); // Check every hour
      }
    },
    onRegisterError(error) {
      console.error("[PWA] Service Worker registration error:", error);
    },
    onNeedRefresh() {
      console.log("[PWA] Update available - showing notification");
      setShowUpdateNotification(true);
    },
    onOfflineReady() {
      console.log("[PWA] App ready to work offline");
    },
  });

  /**
   * Handles the reload action to apply the update.
   */
  const handleReload = async () => {
    if (updateServiceWorker) {
      await updateServiceWorker(true);
    } else {
      window.location.reload();
    }
  };

  /**
   * Handles dismissing the update notification.
   */
  const handleDismiss = () => {
    setShowUpdateNotification(false);
    setNeedRefresh(false);
  };

  // Show notification when update is needed
  useEffect(() => {
    if (needRefresh) {
      setShowUpdateNotification(true);
    }
  }, [needRefresh]);

  if (!showUpdateNotification || !needRefresh) {
    return null;
  }

  return (
    <Alert className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md shadow-lg border-primary/50 bg-background">
      <RefreshCw className="h-4 w-4" />
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <AlertTitle>Update Available</AlertTitle>
          <AlertDescription>
            A new version of the app is available. Reload to get the latest features and improvements.
          </AlertDescription>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={handleReload}
            className="shrink-0"
            aria-label="Reload to apply update"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Reload
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleDismiss}
            className="shrink-0 h-8 w-8 p-0"
            aria-label="Dismiss update notification"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Alert>
  );
};

export default PWAUpdateNotification;

